/**
 * React Query wrapper around `fetchPublicTracking`.
 *
 * Key: `['track', trackingNumber]` — single-source of truth for the tracking
 * cache. The route param is the cache key, so navigating between two
 * tracking numbers swaps cleanly and revisiting an already-looked-up number
 * within `staleTime` is instant.
 *
 * staleTime: 30s — the public tracking endpoint is rate-limited upstream;
 * letting the query stay fresh for 30 s suppresses double-fetches caused by
 * StrictMode + route remounts in dev.
 *
 * `enabled: false` while the input is empty / invalid keeps the hook safe
 * to call from a route component that mounts before the param is parsed.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { fetchPublicTracking } from '../../lib/api-client';
import type { PublicMailItemView } from './PublicMailItemView';
import { isValidTrackingNumber, normaliseTrackingNumber } from './upu-s10';

export function trackingQueryKey(trackingNumber: string): readonly [string, string] {
  return ['track', trackingNumber] as const;
}

export interface UseTrackingQueryOptions {
  /** Override the api client. Used by tests. */
  readonly fetchPublicTrackingImpl?: typeof fetchPublicTracking;
}

export function useTrackingQuery(
  trackingNumber: string,
  options: UseTrackingQueryOptions = {},
): UseQueryResult<PublicMailItemView | null, Error> {
  const normalised = normaliseTrackingNumber(trackingNumber);
  const enabled = isValidTrackingNumber(normalised);
  const impl = options.fetchPublicTrackingImpl ?? fetchPublicTracking;

  return useQuery({
    queryKey: trackingQueryKey(normalised),
    queryFn: () => impl(normalised),
    staleTime: 30_000,
    enabled,
  });
}
